import React, { useState } from "react";
import { Archive, ArchiveRestore, RefreshCw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import * as Dialog from "@radix-ui/react-dialog";

import type { AnalyticsStatus, Recording } from "../api/types";
import { buildSessionTitle, buildSubtitle } from "./analytics/sessionTitle";
import ModalDragStrip from "./ModalDragStrip";

export type RecordingBulkAction = "archive" | "unarchive" | "delete" | "rerun";

type RecordingBulkActionDialogProps = {
  action: RecordingBulkAction;
  recordings: Recording[];
  /** Live analysis status by recording id; recordings currently analyzing are excluded from bulk delete. */
  statusById?: Record<string, AnalyticsStatus>;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  /** Receives the recordings the action will actually be applied to. */
  onConfirm: (recordings: Recording[]) => void | Promise<void>;
};

const RecordingBulkActionDialog = ({
  action,
  recordings,
  statusById,
  busy = false,
  error = null,
  onCancel,
  onConfirm,
}: RecordingBulkActionDialogProps) => {
  const { t } = useTranslation();
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);

  const isDelete = action === "delete";
  // Deleting a recording mid-analysis is unsafe, and re-running one that is
  // already analyzing would just be told a job is in flight.
  const skipsAnalyzing = isDelete || action === "rerun";
  const isSkipped = (recording: Recording) =>
    skipsAnalyzing &&
    ((statusById?.[recording.id] ?? recording.status).status === "analyzing" ||
      recording.rerun?.status === "running");
  const actionable = recordings.filter(recording => !isSkipped(recording));
  const skippedCount = recordings.length - actionable.length;
  const count = actionable.length;
  const Icon = {
    archive: Archive,
    unarchive: ArchiveRestore,
    delete: Trash2,
    rerun: RefreshCw,
  }[action];
  const title = {
    archive: t("bulk_archive_recordings_title", { count }),
    unarchive: t("bulk_unarchive_recordings_title", { count }),
    delete: t("bulk_delete_recordings_title", { count }),
    rerun: t("rerun_recordings_title"),
  }[action];
  const message = {
    archive: t("bulk_archive_recordings_message"),
    unarchive: t("bulk_unarchive_recordings_message"),
    delete: t("bulk_delete_recordings_message"),
    rerun: t("rerun_recordings_message"),
  }[action];
  const confirmLabel = {
    archive: t("archive_recording"),
    unarchive: t("unarchive_recording"),
    delete: t("confirm_delete_recording"),
    rerun: t("rerun_analysis"),
  }[action];
  const confirmDisabled = busy || count === 0 || (isDelete && !deleteAcknowledged);

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !busy) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Content
          asChild
          onEscapeKeyDown={event => {
            if (busy) event.preventDefault();
          }}
        >
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 px-4 py-8"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              tabIndex={-1}
              disabled={busy}
              aria-label={t("cancel")}
              className="absolute inset-0 cursor-default"
              onClick={onCancel}
            />
            <ModalDragStrip />
            <div className="relative z-10 w-full max-w-md rounded-xl border border-outline bg-surface p-5 text-text shadow-2xl">
              <div className="flex items-start gap-3">
                <div
                  className={
                    isDelete
                      ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger"
                      : "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-info/10 text-info"
                  }
                >
                  <Icon size={20} />
                </div>
                <div className="min-w-0">
                  <Dialog.Title asChild>
                    <h2 className="text-lg font-semibold text-text">{title}</h2>
                  </Dialog.Title>
                  <Dialog.Description asChild>
                    <p className="mt-2 text-sm leading-6 text-text-mid">{message}</p>
                  </Dialog.Description>
                </div>
              </div>

              <ul className="mt-4 max-h-56 overflow-y-auto rounded-lg border border-outline">
                {recordings.map(recording => {
                  const skipped = isSkipped(recording);
                  return (
                    <li
                      key={recording.id}
                      className={clsx(
                        "border-b border-outline px-3 py-2 last:border-b-0",
                        skipped && "opacity-60",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <p className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                          {buildSessionTitle(undefined, recording.id, recording)}
                        </p>
                        {skipped && (
                          <span className="inline-flex shrink-0 items-center rounded-full border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning-strong">
                            {t("analyzing_ellipsis")}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-xs tabular-nums text-text-dim">
                        {buildSubtitle(undefined, recording.id, recording)}
                      </p>
                    </li>
                  );
                })}
              </ul>

              {skippedCount > 0 && (
                <p className="mt-3 text-sm leading-5 text-warning-strong">
                  {t("bulk_delete_analyzing_notice", { count: skippedCount })}
                </p>
              )}

              {isDelete && (
                <label className="mt-4 flex items-center gap-2 text-sm leading-5 text-text-mid">
                  <input
                    type="checkbox"
                    checked={deleteAcknowledged}
                    onChange={event => setDeleteAcknowledged(event.target.checked)}
                    className="h-4 w-4 shrink-0 rounded border-outline accent-danger"
                  />
                  <span>{t("acknowledge_delete_recording")}</span>
                </label>
              )}

              {error && <p className="mt-4 text-sm text-danger">{error}</p>}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={onCancel}
                  className="rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text transition hover:bg-muted-surface disabled:opacity-60"
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  disabled={confirmDisabled}
                  onClick={() => onConfirm(actionable)}
                  className={
                    isDelete
                      ? "rounded-lg border border-danger bg-danger px-3 py-2 text-sm font-medium text-white transition hover:bg-danger-strong disabled:cursor-not-allowed disabled:opacity-60"
                      : "rounded-lg border border-info bg-info px-3 py-2 text-sm font-medium text-white transition hover:bg-info-strong disabled:cursor-not-allowed disabled:opacity-60"
                  }
                >
                  {busy ? t("loading_ellipsis") : confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RecordingBulkActionDialog;
